/**
 * Epoch Constants and Utilities
 *
 * Epoch ID represents the number of days since the EPOCH_START_DATE.
 * This ensures consistent epoch calculation across the entire application.
 */

/**
 * Reference date for epoch calculations
 * All epoch IDs are calculated as: floor(days_since_this_date)
 *
 * @example
 * - February 23, 1999 -> epoch_id = 0
 * - February 24, 1999 -> epoch_id = 1
 * - January 15, 2025 -> epoch_id = 9458
 */
export const EPOCH_START_DATE = new Date("1999-02-23");

/**
 * Calculate current epoch ID (days since EPOCH_START_DATE)
 *
 * @returns Current epoch ID based on today's UTC date
 */
export function getCurrentEpochId(): number {
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - EPOCH_START_DATE.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Calculate epoch ID for a specific date
 *
 * @param date - Date to calculate epoch ID for
 * @returns Epoch ID for the given date
 */
export function calculateEpochId(date: Date = new Date()): number {
  const diffTime = Math.abs(date.getTime() - EPOCH_START_DATE.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Calculate the date from an epoch ID
 *
 * @param epochId - Epoch ID to convert to date
 * @returns Date corresponding to the epoch ID
 */
export function getDateFromEpochId(epochId: number): Date {
  const date = new Date(EPOCH_START_DATE);
  date.setDate(date.getDate() + epochId);
  return date;
}
